import { getCurrencySymbol } from "../../../../../utils/utility";

export function getMaxValue(data) {
    const vendorValues = [];
    const quoteDetails = data.countryVendorQuoteDetails;
    Object.keys(quoteDetails).forEach(country => {
        const vendorListValues = Object.values(quoteDetails[country]);

        const sortedVendorsList = vendorListValues.sort((_v1, _v2) => _v2.convertedNetValue - _v1.convertedNetValue);
        vendorValues.push({
            id: quoteDetails[country],
            value: sortedVendorsList[0].convertedNetValue,
        });
    });

    return vendorValues;
}

export function getDefaultVendorMatrixSelections(data, selectedCountryVendor, selectedVendorMaxNetValue, userSelectedVendor) {
    if (!data || !data.countryVendorQuoteDetails) return null;

    let defaultSelections = [];
    let vendorNetValues = [];
    let convertedVendorNetValues = [];

    const quoteDetails = data.countryVendorQuoteDetails;
    Object.keys(quoteDetails).forEach(country => {
        const vendorList = Object.keys(quoteDetails[country]);
        const vendorListValues = Object.values(quoteDetails[country]);

        vendorList.forEach(vendor => {
            const vendorData = quoteDetails[country][vendor];
            const { vendorName = "", convertedNetValue = 0, cnetValue = 0 } = vendorData;
            if (vendorName) {
                const index = vendorNetValues.findIndex(v => v.id === vendorData.vendorName);
                if (index === -1) {
                    // vendor net value not added yet
                    vendorNetValues.push({
                        id: vendorName,
                        value: cnetValue
                    });
                    convertedVendorNetValues.push({
                        id: vendorName,
                        value: convertedNetValue
                    });
                }
                else {
                    // vendor net value already added
                    const currentCnetValue = vendorNetValues[index].value;
                    vendorNetValues[index].value = currentCnetValue + cnetValue;

                    const convertedCurrentNetValue = convertedVendorNetValues[index].value;
                    convertedVendorNetValues[index].value = convertedCurrentNetValue + convertedNetValue;
                }
            }
        });

        // TODO - add logic to determine default country selection
        if (selectedVendorMaxNetValue == 'maxNetValue') {
            const sortedVendorsList = vendorListValues.sort((_v1, _v2) => _v2.convertedNetValue - _v1.convertedNetValue);
            defaultSelections.push({
                id: country,
                value: sortedVendorsList[0].vendorName,
                vendorList: vendorList.join(",")
            });
        } else if (selectedVendorMaxNetValue == 'customizedMaxNetValue') {
            const sortedVendorsList = vendorListValues.sort((_v1, _v2) => _v2.convertedNetValue - _v1.convertedNetValue);
            const countryVendor = vendorListValues.find(vendor => vendor.vendorName === userSelectedVendor);
            defaultSelections.push({
                id: country,
                value: countryVendor ? countryVendor.vendorName : sortedVendorsList[0].vendorName,
                vendorList: vendorList.join(",")
            });
        } else if (selectedCountryVendor) {
            defaultSelections.push({
                id: country,
                value: selectedCountryVendor[country],
                vendorList: vendorList.join(",")
            });
        } else {
            defaultSelections.push({
                id: country,
                value: vendorList[0],
                vendorList: vendorList.join(",")
            });
        }
    });

    vendorNetValues.sort((_v1, _v2) => _v2.value - _v1.value); // sort vendors from highest to lowest cnetvalue

    return {
        defaultSelections,
        vendorNetValues,
        convertedVendorNetValues
    }
}

export function constructUserSelectedTotalVendorNetValue(countryVendorQuoteDetails, userSelectedVendor) {
    const userSelectedTotalVendorNetValue = {};
    Object.keys(countryVendorQuoteDetails).forEach(country => {
        const vendorValuesList = Object.values(countryVendorQuoteDetails[country]);
        let countrySpecificTotalVendorNetValue = 0;
        let vendorSpecificNetValuesToCompare = [];
        const selectedVendorIndex = vendorValuesList.findIndex(vendor => vendor.vendorName === userSelectedVendor);
        if (vendorValuesList.length > 0) {
            //Single Vendor
            if (vendorValuesList.length === 1) {
                vendorSpecificNetValuesToCompare.push(vendorValuesList[0].convertedNetValue);
            }
            //Selected vendor is present in vendor list
            else if (selectedVendorIndex !== -1) {
                vendorSpecificNetValuesToCompare.push(vendorValuesList[selectedVendorIndex].convertedNetValue);
            }
            //No vendor is selected by default
            else {
                vendorValuesList.forEach(vendor => {
                    vendorSpecificNetValuesToCompare.push(vendor.convertedNetValue);
                });
            }
        }

        countrySpecificTotalVendorNetValue = Math.max(...vendorSpecificNetValuesToCompare);
        vendorSpecificNetValuesToCompare = [];
        userSelectedTotalVendorNetValue[country] = countrySpecificTotalVendorNetValue;
    });

    return userSelectedTotalVendorNetValue;
}

export default function vendorMatrix(config) {
    if (!config.data) return null;
    if (!config.data.directQuoteData) return null;

    let result = {};
    let tableData = [];

    const vendorQuoteDetails = config.data.directQuoteData.countryVendorQuoteDetails;
    const countryCurrencyObj = config.data.directQuoteData.countryCurrency;
    const vendorMatrixSelections = config.data.vendorMatrixSelections;
    const quoteStatus = config.data.directQuoteData.status;
    const userSelectedVendor = config.data.userSelectedVendor;
    const selectedVendorMaxNetValue = config.data.selectedVendorMaxNetValue;
    const applicableCountries = Object.keys(vendorQuoteDetails);
    let applicableCostTypes = [];
    if(config.data.isPyxtechEnabled) {
        applicableCostTypes = config.data.hprevitalizeStatus ? ["cnetValue", "chardwareCost", "cprocessingCost", "vhardwareRevitalizeFees", "vhardwareRevitalizeCost", "hpRevenue"] : ["cnetValue", "chardwareCost", "recommendedCustResidualValue", "comparisonCountryRV", "percentAssetWithRV", "cprocessingCost", "hpRevenue"];
    } else {
        applicableCostTypes = config.data.hprevitalizeStatus ? ["cnetValue", "chardwareCost", "cprocessingCost", "vhardwareRevitalizeFees", "vhardwareRevitalizeCost", "hpRevenue"] : ["cnetValue", "chardwareCost", "cprocessingCost", "hpRevenue"];
    }
    const dataProvider = config.data.dataProvider;
    let applicableVendors = [];
    applicableCountries.forEach(country => {
        Object.keys(vendorQuoteDetails[country]).forEach(vendor => {
            if (!applicableVendors.includes(vendor)) {
                applicableVendors.push(vendor);
            }
        });
    });

    applicableCostTypes.forEach(costType => {
        applicableVendors.forEach((vendor, vendorIndex) => {
            let row = [];
            const rowId = `${config.tableType}|${costType}|${vendor}`;

            config.columns.forEach(column => {
                const columnId = column.id;
                let displayAsIs = column.data.displayInBodyAsIs;
                const columnDataType = column.data.displayInBodyAs;
                let highlightColumn = column.data.highlightBodyText;
                let darkBgColumn = column.data.darkBgColumn;
                let columnValue = "";
                let colSpan = 0;
                let rowSpan = 0;
                let addColumnToRow = true;
                let bgColor = "";
                let textColor = "";

                if ((costType === "recommendedCustResidualValue" || costType === "percentAssetWithRV") && vendorIndex > 0) {
                    addColumnToRow = false;
                }

                if (column.isCountryCode) {
                    const countryCode = columnId;
                    if (vendorQuoteDetails[countryCode] && vendorQuoteDetails[countryCode][vendor]) {
                        const countryData = vendorQuoteDetails[countryCode][vendor];
                        const vendorNetValue = countryData[costType] + countryData['clogisticCost'];
                        const currencySymbol = getCurrencySymbol(countryCurrencyObj[column.id]);
                        if (costType === "comparisonCountryRV") {
                            const value = countryData[costType];
                            if (value >= 0) {
                                if (value > 90) {
                                    textColor = "green";
                                    bgColor = "#c6efce";
                                } else if (value >= 50) {
                                    textColor = "#a95c25";
                                    bgColor = "#ffeb9c";
                                } else {
                                    textColor = "red";
                                    bgColor = "#ffc7ce";
                                }
                            }
                        }
                        if ((costType === "comparisonCountryRV" || costType === "recommendedCustResidualValue" || costType === "percentAssetWithRV") && !countryData[costType]) {
                            darkBgColumn = true;
                        }
                        if (costType === "comparisonCountryRV" || costType === "percentAssetWithRV") {
                            columnValue = countryData[costType] ? `${Number(countryData[costType]).toFixed(2)} %` : "";
                        } else if (costType === "cprocessingCost" && countryData['clogisticCost']) {
                            columnValue = vendorNetValue ? `${currencySymbol} ${Number(vendorNetValue).toFixed(2)}` : "";
                        } else if (!isNaN(countryData[costType])) {
                            columnValue = countryData[costType] ? `${currencySymbol} ${Number(countryData[costType]).toFixed(2)}` : "";
                        } else {
                            columnValue = '';
                        }
                        if (countryData.vendorName !== 'TES' && (costType === 'vhardwareRevitalizeFees' || costType === 'vhardwareRevitalizeCost')) {
                            columnValue = ""
                            darkBgColumn = true;
                        }
                    }
                }
                else {
                    if (columnId === "costCategory") {
                        columnValue = costType;
                        displayAsIs = false;
                        rowSpan = applicableVendors.length;
                        addColumnToRow = vendorIndex === 0;
                        highlightColumn = true;
                    }
                    else if (columnId === "vendor") {
                        if (costType === "comparisonCountryRV") {
                            columnValue = `${dataProvider}, ${vendor}`;
                        } else if (costType === "recommendedCustResidualValue" || costType === "percentAssetWithRV") {
                            columnValue = `${dataProvider}`;
                        }
                        else {
                            columnValue = vendor;
                        }
                        darkBgColumn = true;
                    }
                    else {
                        columnValue = "";
                    }
                }

                addColumnToRow && row.push({
                    [columnId]: {
                        id: columnId + "|" + rowId,
                        columnId: columnId,
                        type: columnDataType,
                        value: columnValue,
                        width: column.width,
                        displayAsIs: displayAsIs,
                        showInMobileViewAs: column.showInMobileViewAs,
                        highlightColumn: highlightColumn,
                        darkBgColumn: darkBgColumn,
                        colSpan: colSpan,
                        rowSpan: rowSpan,
                        bgColor: bgColor,
                        textColor: textColor,
                    }
                });
            });

            tableData.push({
                id: rowId,
                data: row
            });
        });
    });

    if (vendorMatrixSelections.length) {
        // if vendor selections available then add manual select controls
        // to let user choose other vendor_selection

        const nonCountryCodeColumns = config.columns.filter(c => !c.isCountryCode);
        let finalRow = [];
        const finalRowId = `${config.tableType}|vendorSelection`;
        finalRow.push({
            vendorSelection: {
                id: "vendorSelection|" + finalRowId,
                columnId: "vendorSelection",
                type: "string",
                value: "vendorSelection",
                displayAsIs: false,
                darkBgColumn: true,
                colSpan: nonCountryCodeColumns.length
            }
        });
        vendorMatrixSelections.forEach(selection => {
            const country = selection.id
            const columnId = "vendorSelection|" + country;
            const options = selection.vendorList.split(",").map(v => ({ id: v, label: v }));
            let columnValueDataType;
            let columnValueData;
            let columnValue;

            if (quoteStatus === "ACCEPTED" || quoteStatus === "LOST") {
                columnValueDataType = "string";
                columnValue = selection.value;
                if (selection.vendorList.split(",").includes(userSelectedVendor) && selectedVendorMaxNetValue === "customizedMaxNetValue") {
                    columnValue = userSelectedVendor;
                }
            } else {
                let defaultValue = selection.value;
                columnValueDataType = "select";
                if (selection.vendorList.split(",").includes(userSelectedVendor) && selectedVendorMaxNetValue === "customizedMaxNetValue") {
                    defaultValue = userSelectedVendor;
                }
                columnValueData = { defaultValue, options };
            }

            finalRow.push({
                [columnId]: {
                    id: columnId + "|" + finalRowId,
                    columnId: columnId,
                    type: columnValueDataType,
                    value: columnValueData || columnValue,
                    displayAsIs: true,
                    clickHandler: event => config.bodyButtonsHandler(event, country)
                }
            });
        });
        tableData.push({
            id: finalRowId,
            data: finalRow
        });
    }

    result['columns'] = config.columns;
    result['data'] = tableData;

    return result;
}
